// Appointment display derivations shared by BOTH render surfaces (web components
// and src/components/mobile/). Render trees are forked by design; the vocabulary —
// urgency labels, duration phrasing, the hero's escalation model — must not be.
import { C } from '@parsina/framework/clinical/src/constants/theme';
import { GRADIENT_BRAND, CT } from '@parsina/framework/tokens';
import { visitOverrun, waitedMinutes } from '../hooks/useReception';

// ── A note on `t` ────────────────────────────────────────────────────────────
//
// The derivations below take the translator as an argument rather than reaching
// for it. They are plain functions shared by two render trees and covered by
// unit tests that call them directly, so they cannot use a hook — and making
// them return translation KEYS instead would push the vocabulary back out to
// the two surfaces, which is exactly what this module exists to prevent.
//
// Passing `t` in keeps them pure: same inputs, same output, and a test can
// assert the Persian and the English wording of the same model.

// Terminal. `rejected` joins the two originals: the clinic declined the patient's
// request, or it expired unanswered — either way nothing further happens to the
// row, and leaving it out of here parks a dead appointment at the top of the
// doctor's day forever.
//
// `no_show` is terminal for exactly the same reason. The desk marks «عدم‌حضور»
// (scheduling's MarkAppointmentNoShow, from `scheduled` or `arrived`), and the
// day list really does receive one — constants/statuses.js already carries its
// label. Left out of here the row sat in «پیش رو» all day, never reached
// «پایان‌یافته», and `nextApt` could announce it as the patient to call in next
// while the clinic panel — whose own DONE_FLOWS does include it — showed the
// same row as finished. Terminal here does NOT mean unreopenable: scheduling's
// REOPENABLE_STATUSES contains `completed` and `cancelled` too.
export const isDone = s => s === 'completed' || s === 'cancelled' || s === 'rejected' || s === 'no_show';
export const isHere = s => s === 'arrived' || s === 'in_visit';

// A row the doctor may READ but not act on.
//
// `requested` is a patient asking for a slot from their app; confirming it is
// reception's job and nobody else's (design §17 D12) — the desk owns the calendar
// and the desk rules, and a second confirming surface splits the inbox.
// Scheduling enforces the same thing from the other side: `mark_arrived` from
// `requested` is refused outright. `rejected` is terminal, so there is nothing to
// act on there either.
export const isReadOnly = s => s === 'requested' || s === 'rejected';

// The one exception to «read-only»: a `requested` row the DOCTOR booked
// (source doctor_app) at a clinic that withdrew their booking grant. Confirming
// it is still the desk's; withdrawing their own ask is the doctor's.
export const isOwnPendingRequest = apt => apt?.status === 'requested' && apt?.source === 'doctor_app';

// A patient kept waiting past this reads as a problem, not a queue.
export const WAIT_WARN_MIN = 20;

export const URGENCY_BADGE = {
  emergency: { labelKey: 'common.urgency.emergency', color: CT.dangerInk, bg: C.redLight },
  urgent:    { labelKey: 'common.urgency.urgent',    color: CT.warnInk,   bg: C.amberLight },
};

/**
 * A minute count as a duration phrase, in the active language.
 *
 * The digits are localised by the translator's own number interpolation, so
 * this no longer folds them to Persian by hand — «۹۰ دقیقه» in Persian and
 * «1 h 30 m» in English come from the same call.
 */
export const formatDuration = (item, min) => {
  if (min == null || !Number.isFinite(Number(min))) return '';
  const total = Math.max(0, Math.round(Number(min)));
  if (total < 60) return item('common.duration.minutes', { count: total });
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  return minutes === 0
    ? item('common.duration.hours', { hours })
    : item('common.duration.hoursMinutes', { hours, minutes });
};

const toMinutes = (t = '00:00') => {
  const [h, m] = t.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};

// The next-patient hero model: who the doctor calls in next and — when a visit is
// open — how far past its slot it has run. normal (within slot) → over (past it)
// → critical (past 2×), with «پایان ویزیت» promoted as closing becomes as likely
// the right action as continuing.
export function buildHeroModel(apt, nowMin, nowMs, item) {
  const inVisit = apt.status === 'in_visit';
  const present = isHere(apt.status);
  const { elapsedMin, level } = inVisit ? visitOverrun(apt, nowMs) : { elapsedMin: null, level: 'normal' };
  const waited = apt.status === 'arrived' ? waitedMinutes(apt, nowMs) : null;

  const diff = toMinutes(apt.time) - nowMin;
  const waitLabel = inVisit
    ? item('dashboard.hero.inYourRoom')
    : present
      ? (waited != null
        ? item('dashboard.hero.waitingSince', { count: waited })
        : item('dashboard.hero.waitingForYou'))
      : diff <= 0
        ? item('dashboard.hero.rightNow')
        : item('dashboard.hero.startsIn', { duration: formatDuration(item, diff) });

  return {
    inVisit, present, elapsedMin, level, waitLabel,
    heroLabel: inVisit
      ? item('dashboard.hero.inVisit')
      : present ? item('dashboard.hero.nextIn') : item('dashboard.hero.nextAppointment'),
    urg: URGENCY_BADGE[apt.urgency],
    initials: (apt.name || item('common.labels.patient')).split(' ').map(p => p[0]).join('').slice(0, 2),
    background: level === 'critical' ? C.red : GRADIENT_BRAND,
    dotColor: level === 'critical' ? C.redLight : level === 'over' ? C.amberLight : C.tealLight,
  };
}
